import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import type { RemoteConfig } from "../types";
import remoteConfigFile from "../../remotes.config.json";

const STORAGE_KEY = "mfe_shell_user_apps";
const PERMISSIONS_CACHE_KEY = "mfe_shell_permissions_cache";
const API = (process.env.SHELL_REGISTRY_API_URL as string) ?? "";

const builtInRemotes: RemoteConfig[] = (remoteConfigFile as { remotes: RemoteConfig[] }).remotes;

export type SubmissionStatus = "local" | "pending" | "approved" | "rejected";

export interface LocalApp extends RemoteConfig {
  submissionId?: string;
  submissionStatus?: SubmissionStatus;
}

export interface OrgApp extends RemoteConfig {
  id: string;
  submitted_by?: string;
}

const ROLE_PERMISSIONS: Record<string, string[]> = {
  'Deployment Coordinators': ['deployment:create'],
};

interface RemoteRegistryContextValue {
  allRemotes: RemoteConfig[];
  builtInRemotes: RemoteConfig[];
  userApps: LocalApp[];
  orgApps: OrgApp[];
  pendingCount: number;
  isApprover: boolean;
  isAdmin: boolean;
  userPermissions: string[];
  addApp: (app: Omit<RemoteConfig, "name" | "route">) => void;
  updateAppIcon: (name: string, icon: string) => void;
  updateOrgAppIcon: (id: string, icon: string) => Promise<void>;
  removeApp: (name: string) => void;
  submitForApproval: (name: string) => Promise<void>;
  approvePending: (id: string) => Promise<void>;
  rejectPending: (id: string) => Promise<void>;
  revokeApp: (id: string) => Promise<void>;
  refreshPending: () => Promise<void>;
  pendingApps: OrgSubmission[];
}

export interface OrgSubmission {
  id: string;
  name: string;
  label: string;
  url: string;
  type: "iframe" | "mfe";
  icon: string;
  route: string;
  submitted_by: string;
  submitted_by_name?: string;
  submitted_at: string;
}

const RemoteRegistryContext = createContext<RemoteRegistryContextValue | null>(null);

function labelToSlug(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function loadUserApps(): LocalApp[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveUserApps(apps: LocalApp[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(apps));
}

interface CachedPermissions {
  orgApps: OrgApp[];
  isApprover: boolean;
  isAdmin: boolean;
  userPermissions: string[];
}

function loadCachedPermissions(email: string): CachedPermissions | null {
  try {
    const raw = localStorage.getItem(PERMISSIONS_CACHE_KEY);
    if (!raw) return null;
    const all = JSON.parse(raw) as Record<string, CachedPermissions>;
    return all[email] ?? null;
  } catch { return null; }
}

const EMPTY_CACHED_PERMISSIONS: CachedPermissions = { orgApps: [], isApprover: false, isAdmin: false, userPermissions: [] };

function patchCachedPermissions(email: string, patch: Partial<CachedPermissions>): void {
  try {
    const raw = localStorage.getItem(PERMISSIONS_CACHE_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, CachedPermissions>) : {};
    all[email] = { ...EMPTY_CACHED_PERMISSIONS, ...all[email], ...patch };
    localStorage.setItem(PERMISSIONS_CACHE_KEY, JSON.stringify(all));
  } catch { /* ignore quota/serialization errors */ }
}

export function RemoteRegistryProvider({ children, userEmail, userName }: { children: React.ReactNode; userEmail?: string; userName?: string }) {
  const [userApps, setUserApps] = useState<LocalApp[]>(loadUserApps);
  const [orgApps, setOrgApps] = useState<OrgApp[]>(() => (userEmail && loadCachedPermissions(userEmail)?.orgApps) || []);
  const [isApprover, setIsApprover] = useState(() => (userEmail && loadCachedPermissions(userEmail)?.isApprover) || false);
  const [isAdmin, setIsAdmin] = useState(() => (userEmail && loadCachedPermissions(userEmail)?.isAdmin) || false);
  const [userPermissions, setUserPermissions] = useState<string[]>(() => (userEmail && loadCachedPermissions(userEmail)?.userPermissions) || []);
  const [pendingApps, setPendingApps] = useState<OrgSubmission[]>([]);

  useEffect(() => { saveUserApps(userApps); }, [userApps]);

  // Fetch approved org apps once we know the user (visibility is role-gated)
  useEffect(() => {
    if (!userEmail) return;
    fetch(`${API}/api/shell/apps/approved?email=${encodeURIComponent(userEmail)}`)
      .then((r) => r.json())
      .then((data) => {
        const apps = data.apps ?? [];
        setOrgApps(apps);
        patchCachedPermissions(userEmail, { orgApps: apps });
      })
      .catch(() => {});
  }, [userEmail]);

  // Sync userApps status when orgApps changes: mark pending apps as approved if their URL is now in orgApps
  useEffect(() => {
    if (orgApps.length === 0) return;
    const orgUrls = new Set(orgApps.map((o) => o.url));
    setUserApps((prev) => {
      const updated = prev.map((a) =>
        orgUrls.has(a.url) && a.submissionStatus === "pending"
          ? { ...a, submissionStatus: "approved" as SubmissionStatus }
          : a
      );
      return updated.some((a, i) => a.submissionStatus !== prev[i].submissionStatus) ? updated : prev;
    });
  }, [orgApps]);

  // Record login for user directory
  useEffect(() => {
    if (!userEmail) return;
    fetch(`${API}/api/shell/users/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: userEmail, name: userName }),
    }).catch(() => {});
  }, [userEmail, userName]);

  // Check approver/admin roles and user permissions
  useEffect(() => {
    if (!userEmail) return;
    fetch(`${API}/api/shell/approvers/me?email=${encodeURIComponent(userEmail)}`)
      .then((r) => r.json())
      .then((data) => {
        const approver = !!data.isApprover;
        setIsApprover(approver);
        patchCachedPermissions(userEmail, { isApprover: approver });
        if (approver) refreshPendingInternal(userEmail);
      })
      .catch(() => {});
    fetch(`${API}/api/shell/admin/me?email=${encodeURIComponent(userEmail)}`)
      .then((r) => r.json())
      .then((data) => {
        const admin = !!data.isAdmin;
        setIsAdmin(admin);
        patchCachedPermissions(userEmail, { isAdmin: admin });
      })
      .catch(() => {});
    fetch(`${API}/api/shell/users/roles?email=${encodeURIComponent(userEmail)}`)
      .then((r) => r.json())
      .then((data: { roles?: string[] }) => {
        const perms = [...new Set((data.roles ?? []).flatMap((role) => ROLE_PERMISSIONS[role] ?? []))];
        setUserPermissions(perms);
        patchCachedPermissions(userEmail, { userPermissions: perms });
      })
      .catch(() => {});
  }, [userEmail]);

  async function refreshPendingInternal(email: string) {
    const r = await fetch(`${API}/api/shell/apps/pending?email=${encodeURIComponent(email)}`);
    if (r.ok) {
      const data = await r.json();
      setPendingApps(data.apps ?? []);
    }
  }

  const refreshPending = useCallback(async () => {
    if (userEmail) await refreshPendingInternal(userEmail);
  }, [userEmail]);

  const addApp = useCallback((app: Omit<RemoteConfig, "name" | "route">) => {
    const slug = labelToSlug(app.label);
    const allNames = new Set([
      ...builtInRemotes.map((r) => r.name),
      ...orgApps.map((r) => r.name),
      ...loadUserApps().map((r) => r.name),
    ]);
    let name = slug;
    let counter = 2;
    while (allNames.has(name)) name = `${slug}-${counter++}`;
    setUserApps((prev) => [...prev, { ...app, name, route: `/${name}`, submissionStatus: "local" }]);
  }, [orgApps]);

  const updateAppIcon = useCallback((name: string, icon: string) => {
    setUserApps((prev) => prev.map((a) => (a.name === name ? { ...a, icon } : a)));
  }, []);

  const updateOrgAppIcon = useCallback(async (id: string, icon: string) => {
    if (!userEmail) throw new Error("Registry API not configured");
    const r = await fetch(`${API}/api/shell/apps/${id}/icon`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: userEmail, icon }),
    });
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      throw new Error(data.error || "Failed to update icon");
    }
    setOrgApps((prev) => prev.map((a) => (a.id === id ? { ...a, icon } : a)));
  }, [userEmail]);

  const removeApp = useCallback((name: string) => {
    setUserApps((prev) => prev.filter((a) => a.name !== name));
  }, []);

  const submitForApproval = useCallback(async (name: string) => {
    if (!userEmail) throw new Error("Registry API not configured");
    const app = loadUserApps().find((a) => a.name === name);
    if (!app) throw new Error("App not found");
    const r = await fetch(`${API}/api/shell/apps`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: app.name, label: app.label, url: app.url, type: app.type,
        icon: app.icon, scope: app.scope, module: app.module, route: app.route,
        submittedBy: userEmail,
        submittedByName: undefined,
      }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || "Submission failed");
    setUserApps((prev) =>
      prev.map((a) => a.name === name ? { ...a, submissionId: data.id, submissionStatus: "pending" } : a)
    );
  }, [userEmail]);

  const approvePending = useCallback(async (id: string) => {
    if (!userEmail) return;
    await fetch(`${API}/api/shell/apps/${id}/approve`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: userEmail }),
    });
    // Refresh org apps and pending queue
    const approved = await fetch(`${API}/api/shell/apps/approved`).then((r) => r.json());
    setOrgApps(approved.apps ?? []);
    if (userEmail) await refreshPendingInternal(userEmail);
  }, [userEmail]);

  const rejectPending = useCallback(async (id: string) => {
    if (!userEmail) return;
    await fetch(`${API}/api/shell/apps/${id}/reject`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: userEmail }),
    });
    if (userEmail) await refreshPendingInternal(userEmail);
  }, [userEmail]);

  const revokeAppFn = useCallback(async (id: string) => {
    if (!userEmail) return;
    await fetch(`${API}/api/shell/apps/${id}/revoke`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: userEmail }),
    });
    const approved = await fetch(`${API}/api/shell/apps/approved`).then((r) => r.json());
    setOrgApps(approved.apps ?? []);
  }, [userEmail]);

  // Merge: built-ins + org-approved (dedup by url) + user local apps (dedup by url)
  const orgAppsFiltered = orgApps.filter((o) => !builtInRemotes.some((b) => b.url === o.url));
  const allUrls = new Set([...builtInRemotes.map((r) => r.url), ...orgAppsFiltered.map((r) => r.url)]);
  const userAppsFiltered = userApps.filter((a) => !allUrls.has(a.url));
  const allRemotes: RemoteConfig[] = [...builtInRemotes, ...orgAppsFiltered, ...userAppsFiltered];
  const pendingCount = pendingApps.length;

  return (
    <RemoteRegistryContext.Provider value={{
      allRemotes, builtInRemotes, userApps, orgApps: orgAppsFiltered,
      pendingCount, isApprover, isAdmin, userPermissions, addApp, updateAppIcon, updateOrgAppIcon, removeApp,
      submitForApproval, approvePending, rejectPending, refreshPending,
      pendingApps, revokeApp: revokeAppFn,
    }}>
      {children}
    </RemoteRegistryContext.Provider>
  );
}

export function useRemoteRegistry(): RemoteRegistryContextValue {
  const ctx = useContext(RemoteRegistryContext);
  if (!ctx) throw new Error("useRemoteRegistry must be used inside RemoteRegistryProvider");
  return ctx;
}
